import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Icon, Pill, Thumb, cx } from "../../catalogProductStudio/edit/StudioUi";
import { getGroupSelectionState, isSelectableItem, lineQuantity } from "../state/printSelectionModel";
import { ItemCounts, ItemIdentity, TriStateCheckbox, blockedReasonText } from "../PrintCenterUi";
import { fill, plural } from "../printCenterModel";

const toggleGroup = (items, entries, onSelect, onDeselect) => {
  const selectable = items.filter(isSelectableItem);
  if (getGroupSelectionState(selectable, entries) === "all") onDeselect(selectable.map((item) => item.key));
  else onSelect(selectable.filter((item) => !entries[item.key]));
};

/** Colours × sizes. Click a colour, a size or a single cell. */
function VariantMatrix({ tt, product, entries, onSelect, onDeselect }) {
  const { colors, sizes } = product.matrix;
  const cell = new Map(product.items.map((item) => [`${item.colorValueId}:${item.sizeValueId}`, item]));
  const columnItems = (sizeId) => product.items.filter((item) => item.sizeValueId === sizeId);
  const rowItems = (colorId) => product.items.filter((item) => item.colorValueId === colorId);

  return (
    <div className="pc-matrix-wrap">
      <table className="pc-matrix">
        <thead>
          <tr>
            <th scope="col" className="pc-matrix__corner">
              {tt("matrixColorSize")}
            </th>
            {sizes.map((size) => {
              const state = getGroupSelectionState(columnItems(size.valueId), entries);
              return (
                <th key={size.valueId} scope="col">
                  <button
                    type="button"
                    className={cx("pc-matrix__size", state !== "none" && `is-${state}`)}
                    onClick={() => toggleGroup(columnItems(size.valueId), entries, onSelect, onDeselect)}
                    aria-label={fill(tt("selectSize"), { size: size.value })}
                    aria-pressed={state === "all" ? true : state === "some" ? "mixed" : false}
                  >
                    {size.value}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {colors.map((color) => {
            const items = rowItems(color.valueId);
            return (
              <tr key={color.valueId}>
                <th scope="row">
                  <label className="pc-matrix__color">
                    <TriStateCheckbox
                      state={getGroupSelectionState(items, entries)}
                      onChange={() => toggleGroup(items, entries, onSelect, onDeselect)}
                      label={fill(tt("selectColor"), { color: color.value })}
                      disabled={!items.some(isSelectableItem)}
                    />
                    <Thumb src={color.imageUrl} size={30} fallbackLabel={color.value} />
                    <span>{color.value}</span>
                  </label>
                </th>
                {sizes.map((size) => {
                  const item = cell.get(`${color.valueId}:${size.valueId}`);
                  if (!item) {
                    return (
                      <td key={size.valueId} className="pc-matrix__empty" aria-label={tt("matrixNoVariant")}>
                        —
                      </td>
                    );
                  }
                  const entry = entries[item.key];
                  const blocked = !isSelectableItem(item);
                  return (
                    <td key={size.valueId}>
                      <button
                        type="button"
                        className={cx(
                          "pc-cell",
                          entry && "is-selected",
                          blocked && "is-blocked",
                          item.onHand <= 0 && "is-empty",
                          item.demand?.outstanding > 0 && "has-demand"
                        )}
                        aria-pressed={Boolean(entry)}
                        aria-disabled={blocked}
                        aria-label={[
                          `${color.value} · ${size.value}`,
                          fill(tt("cellOnHand"), { count: item.onHand }),
                          item.demand?.outstanding > 0 ? plural(tt, "needsLabelsCount", item.demand.outstanding) : null,
                          entry ? plural(tt, "labelCount", lineQuantity(entry)) : null,
                          blocked ? blockedReasonText(tt, item) : null,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                        title={blocked ? blockedReasonText(tt, item) : `${item.barcode}`}
                        onClick={() => {
                          if (blocked) return;
                          if (entry) onDeselect([item.key]);
                          else onSelect([item]);
                        }}
                      >
                        <span className="pc-cell__stock">{item.onHand}</span>
                        {item.demand?.outstanding > 0 ? (
                          <span className="pc-cell__demand">+{item.demand.outstanding}</span>
                        ) : null}
                        {entry ? <span className="pc-cell__qty">×{lineQuantity(entry)}</span> : null}
                        {blocked ? <Icon name="alert" size={13} className="pc-cell__alert" /> : null}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Products that are not a clean colour × size grid, and serialized units. */
function VariantList({ tt, product, entries, onSelect, onDeselect }) {
  return (
    <ul className="pc-variant-list">
      {product.items.map((item) => {
        const entry = entries[item.key];
        const blocked = !isSelectableItem(item);
        return (
          <li key={item.key} className={cx("pc-variant-row", entry && "is-selected", blocked && "is-blocked")}>
            <input
              type="checkbox"
              className="pc-check"
              checked={Boolean(entry)}
              disabled={blocked}
              aria-label={fill(tt("selectItem"), { name: item.variantTitle || item.productTitle })}
              onChange={() => (entry ? onDeselect([item.key]) : onSelect([item]))}
            />
            <ItemIdentity tt={tt} item={item} compact showProduct={false} />
            <ItemCounts tt={tt} item={item} />
            {blocked ? (
              <span className="pc-blocked-note">
                <Icon name="alert" size={14} />
                {blockedReasonText(tt, item)}
                <Link to={`/admin/catalog/products/${product.id}`} className="pc-link">
                  {tt("openProduct")}
                </Link>
              </span>
            ) : entry ? (
              <span className="pc-qty-tag">{fill(tt("printQtyTag"), { count: lineQuantity(entry) })}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function PrintProductCard({ tt, product, entries, onSelect, onDeselect, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const selectable = useMemo(() => product.items.filter(isSelectableItem), [product.items]);
  const state = getGroupSelectionState(product.items, entries);
  const selectedCount = product.items.filter((item) => entries[item.key]).length;
  const blockedCount = product.items.length - selectable.length;

  return (
    <article className={cx("pc-product", state !== "none" && "has-selection")}>
      <header className="pc-product__head">
        <TriStateCheckbox
          state={state}
          onChange={() => toggleGroup(product.items, entries, onSelect, onDeselect)}
          label={fill(tt("selectProduct"), { name: product.title })}
          disabled={!selectable.length}
        />
        <button
          type="button"
          className="pc-product__toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          <Thumb src={product.imageUrl} size={52} fallbackLabel={product.title} />
          <span className="pc-product__titles">
            <strong className="pc-product__title">{product.title}</strong>
            <span className="pc-product__meta">
              {[product.baseSku, product.brandName, product.categoryName].filter(Boolean).join(" · ")}
            </span>
            <span className="pc-product__badges">
              {product.status !== "active" ? (
                <Pill tone={product.status === "draft" ? "warning" : "neutral"}>{tt(`status_${product.status}`)}</Pill>
              ) : null}
              {product.inventoryMode === "serialized" ? <Pill tone="info">{tt("serializedPill")}</Pill> : null}
              <span className="pc-muted pc-small">{plural(tt, "itemCount", product.totals.items)}</span>
              <span className="pc-muted pc-small">{fill(tt("onHandTotal"), { count: product.totals.onHand })}</span>
              {product.totals.outstanding > 0 ? (
                <Pill tone="warning" icon="tag">
                  {plural(tt, "needsLabelsCount", product.totals.outstanding)}
                </Pill>
              ) : null}
              {blockedCount ? (
                <Pill tone="danger" icon="alert">
                  {plural(tt, "blockedCount", blockedCount)}
                </Pill>
              ) : null}
            </span>
          </span>
          <span className="pc-product__side">
            {selectedCount ? (
              <span className="pc-selected-count">{fill(tt("selectedOf"), { selected: selectedCount, total: product.items.length })}</span>
            ) : null}
            <Icon name={expanded ? "chevronDown" : "chevronRight"} size={18} />
          </span>
        </button>
      </header>

      {expanded ? (
        <div className="pc-product__body">
          {!product.items.length ? (
            <p className="pc-muted pc-small">
              {product.inventoryMode === "serialized" ? tt("noUnitsInStock") : tt("noVariants")}
            </p>
          ) : product.matrix ? (
            <VariantMatrix tt={tt} product={product} entries={entries} onSelect={onSelect} onDeselect={onDeselect} />
          ) : (
            <VariantList tt={tt} product={product} entries={entries} onSelect={onSelect} onDeselect={onDeselect} />
          )}
        </div>
      ) : null}
    </article>
  );
}

export default PrintProductCard;
